import type { ChoroplethLayerDef } from './types';
import type { ComuneFull } from '@/data';

// Палитры подобраны по стандартным картографическим конвенциям —
// то, что используют Apple Maps, USGS, NOAA, weather-карты.
//
// - Высота: гипсометрические тона (зелёные низины → коричневые горы → белые ледники)
// - Температура: NOAA-style diverging (тёмно-синий холод → тёмно-красное пекло)
// - Влажность/осадки/снег: насыщенный синий-фиолетовый (вода = синий)
// - Солнце: жёлтый→оранжевый (солнечная "тёплая" гамма, без алармизма)
// - Цены: red-heat (Zillow-style)
// - Скорость интернета: traffic-light (RdYlGn)
// - Расстояния (close=good) + tax: RdYlGn flipped + inverse flag
// - Плотность населения: magma reversed (классический heatmap)
//
// Refs: ColorBrewer 2.0, NOAA precip/temp, Apple Weather, USGS topo, viridis family.

// Стандартный температурный gradient: dark blue (cold) → dark red (hot)
const TEMP_DIVERGING = ['#08306b', '#4292c6', '#deebf7', '#fee090', '#fc8d59', '#a50026'];

// Общая температурная шкала для всех T° слоёв (mean/jul/jan).
// 10°C везде выглядит одинаково — нет «синего июля» из-за per-layer normalize.
// Покрывает весь годовой диапазон: суровая зима (-12) до жары (+30).
const TEMP_STOPS_UNIFIED = [-12, -3, 5, 14, 22, 30];

// Гипсометрические тона для высоты (terrain colors)
const HYPSOMETRIC = ['#3a9b46', '#8fc97d', '#dfd592', '#bd8a4f', '#7a4d2c', '#e8e8e8'];

// Light → dark blue (вода / влага / снег)
const BLUES_DEEP = ['#f7fbff', '#deebf7', '#9ecae1', '#4292c6', '#08519c', '#08306b'];

// Насыщенный фиолетово-синий (влажность)
const PURPLE_BLUE = ['#fff7fb', '#d0d1e6', '#a6bddb', '#3690c0', '#0570b0', '#023858'];

// Жёлтый→бронза (солнце)
const SUN_YELLOW = ['#ffffd4', '#fee391', '#fec44f', '#fe9929', '#d95f0e', '#993404'];

// Цены (Oranges и Reds — для rent и sale соответственно)
const PRICE_ORANGES = ['#fff5eb', '#fdd0a2', '#fdae6b', '#fd8d3c', '#e6550d', '#a63603'];
const PRICE_REDS = ['#fee5d9', '#fcbba1', '#fc9272', '#fb6a4a', '#de2d26', '#a50f15'];

// Доход (зелёное)
const MONEY_GREEN = ['#ffffe5', '#f7fcb9', '#d9f0a3', '#addd8e', '#41ab5d', '#005a32'];

// Traffic-light RdYlGn (медленно→быстро / далеко→близко flipped)
const TRAFFIC_BAD_FIRST = ['#d73027', '#fc8d59', '#fee08b', '#d9ef8b', '#91cf60', '#1a9850'];
const TRAFFIC_GOOD_FIRST = ['#1a9850', '#91cf60', '#d9ef8b', '#fee08b', '#fc8d59', '#d73027'];

// Magma reversed — классический heatmap для density (низ=светлый жёлтый → max=тёмно-фиолетовый)
const HEATMAP_MAGMA = ['#fcfdbf', '#feb78a', '#f8765c', '#bb3754', '#56106e', '#000004'];

// Foreign residents (total + RU/UA/BY) — единая палитра, т.к. это один тип
// сущности (национальности), слои взаимоисключающие. Разная градация — через stops.
const FOREIGN_PALETTE = ['#edf8fb', '#ccece6', '#99d8c9', '#66c2a4', '#2ca25f', '#006d2c'];

const layers: ChoroplethLayerDef[] = [
  // ─── Climate ─────────────────────────────────────────────────────────────
  {
    id: 'altitude',
    group: 'climate',
    label: 'Высота',
    unit: 'м',
    stops: [0, 200, 500, 1000, 2000, 3500],
    palette: HYPSOMETRIC, // зелёные равнины → коричневые горы → серый/белый ледник
    getValue: (c: ComuneFull) => c.alt,
  },
  {
    id: 't_mean',
    group: 'climate',
    label: 'Средняя T° (год)',
    unit: '°C',
    stops: TEMP_STOPS_UNIFIED, // общая шкала для всех T° слоёв
    palette: TEMP_DIVERGING,
    getValue: (c: ComuneFull) => c.climate.t_mean,
  },
  {
    id: 't_jul',
    group: 'climate',
    label: 'T° июля',
    unit: '°C',
    stops: TEMP_STOPS_UNIFIED,
    palette: TEMP_DIVERGING,
    getValue: (c: ComuneFull) => c.climate.t_jul,
  },
  {
    id: 't_jan',
    group: 'climate',
    label: 'T° января',
    unit: '°C',
    stops: TEMP_STOPS_UNIFIED,
    palette: TEMP_DIVERGING,
    getValue: (c: ComuneFull) => c.climate.t_jan,
  },
  {
    id: 'humidity',
    group: 'climate',
    label: 'Влажность',
    unit: '%',
    stops: [40, 55, 65, 75, 85, 95],
    palette: PURPLE_BLUE, // насыщенный сине-фиолетовый — стандарт для воды/влаги
    getValue: (c: ComuneFull) => c.climate.hum ?? null,
  },
  {
    id: 'precip',
    group: 'climate',
    label: 'Осадки (год)',
    unit: 'мм',
    stops: [300, 600, 900, 1200, 1800, 2500],
    palette: BLUES_DEEP, // светло-голубой сухо → тёмно-синий мокро (карты осадков)
    getValue: (c: ComuneFull) => c.climate.precip ?? null,
  },
  {
    id: 'snow',
    group: 'climate',
    label: 'Снежных дней',
    unit: 'дн/год',
    stops: [0, 5, 15, 30, 60, 100],
    palette: BLUES_DEEP,
    getValue: (c: ComuneFull) => c.climate.snow ?? null,
  },
  {
    id: 'sunny',
    group: 'climate',
    label: 'Солнечных дней',
    unit: 'дн/год',
    stops: [80, 120, 150, 180, 220, 270],
    palette: SUN_YELLOW, // солнечная гамма — без красного-«опасности»
    getValue: (c: ComuneFull) => c.climate.sunny ?? null,
  },

  // ─── Economy ──────────────────────────────────────────────────────────────
  {
    id: 'rent',
    group: 'economy',
    label: 'Аренда €/м²/год',
    unit: '€/м²',
    stops: [30, 50, 70, 90, 110, 150],
    palette: PRICE_ORANGES, // Zillow-style: дёшево=светло → дорого=насыщенно
    getValue: (c: ComuneFull) => c.econ?.rent_eur_m2y ?? null,
  },
  {
    id: 'sale',
    group: 'economy',
    label: 'Продажа €/м²',
    unit: '€/м²',
    stops: [500, 1000, 1500, 2500, 4000, 7000],
    palette: PRICE_REDS, // отличается от аренды (другой hue)
    getValue: (c: ComuneFull) => c.econ?.sale_eur_m2 ?? null,
  },
  {
    id: 'income',
    group: 'economy',
    label: 'Средний доход',
    unit: '€/год',
    stops: [10000, 14000, 18000, 22000, 28000, 35000],
    palette: MONEY_GREEN, // деньги — зелёное
    getValue: (c: ComuneFull) => c.econ?.income_eur ?? null,
  },

  // ─── Infra ────────────────────────────────────────────────────────────────
  {
    id: 'internet',
    group: 'infra',
    label: 'Интернет',
    unit: 'Мбит/с',
    stops: [0, 10, 30, 100, 500, 1000],
    palette: TRAFFIC_BAD_FIRST, // светофор: медленно=красный → быстро=зелёный
    getValue: (c: ComuneFull) => c.internet?.dl ?? null,
  },
  {
    id: 'hospital',
    group: 'infra',
    label: 'До больницы',
    unit: 'км',
    inverse: true,
    stops: [0, 5, 15, 30, 60, 120],
    palette: TRAFFIC_GOOD_FIRST, // близко=зелёный → далеко=красный
    getValue: (c: ComuneFull) => c.infra.hosp,
  },
  {
    id: 'train',
    group: 'infra',
    label: 'До ЖД-станции',
    unit: 'км',
    inverse: true,
    stops: [0, 2, 8, 20, 50, 100],
    palette: TRAFFIC_GOOD_FIRST,
    getValue: (c: ComuneFull) => c.infra.train,
  },

  // ─── Demo ─────────────────────────────────────────────────────────────────
  {
    id: 'density',
    group: 'demo',
    label: 'Плотность населения',
    unit: 'чел/км²',
    stops: [0, 50, 150, 400, 1000, 3000],
    palette: HEATMAP_MAGMA, // классический heatmap: бледный → горячий → тёмный
    getValue: (c: ComuneFull) => c.demo.density,
  },
  {
    id: 'foreign_total',
    group: 'demo',
    label: 'Иностранцы (% жителей)',
    unit: '%',
    stops: [0, 2, 5, 10, 15, 25],
    palette: FOREIGN_PALETTE, // единая палитра для всех foreign_* слоёв
    getValue: (c: ComuneFull) => {
      const f = c.foreign;
      if (!f || f.total == null || !c.pop) return null;
      return (f.total / c.pop) * 100;
    },
  },
  {
    id: 'foreign_ru',
    group: 'demo',
    label: 'Русскоязычные (Россия %)',
    unit: '%',
    stops: [0, 0.05, 0.1, 0.2, 0.5, 1.0],
    palette: FOREIGN_PALETTE,
    getValue: (c: ComuneFull) => c.foreign?.ru_pct ?? null,
  },
  {
    id: 'foreign_ua',
    group: 'demo',
    label: 'Украинцы (%)',
    unit: '%',
    stops: [0, 0.1, 0.3, 0.6, 1.2, 3.0],
    palette: FOREIGN_PALETTE,
    getValue: (c: ComuneFull) => c.foreign?.ua_pct ?? null,
  },
  {
    id: 'foreign_by',
    group: 'demo',
    label: 'Белорусы (%)',
    unit: '%',
    stops: [0, 0.02, 0.05, 0.1, 0.2, 0.5],
    palette: FOREIGN_PALETTE,
    getValue: (c: ComuneFull) => c.foreign?.by_pct ?? null,
  },

  // ─── Tax (MEF addizionale comunale IRPEF) ──────────────────────────────────
  {
    id: 'irpef',
    group: 'tax',
    label: 'Доп. IRPEF (% от дохода)',
    unit: '%',
    inverse: true,
    stops: [0, 0.4, 0.6, 0.8, 1.0, 1.2],
    palette: TRAFFIC_GOOD_FIRST, // меньше налог = зелёный
    getValue: (c: ComuneFull) => c.tax?.irpef_pct ?? null,
  },
];

export const LAYERS_BY_ID: Record<string, ChoroplethLayerDef> = Object.fromEntries(
  layers.map((l) => [l.id, l]),
);

export const LAYER_GROUPS: Record<string, ChoroplethLayerDef[]> = layers.reduce(
  (acc, l) => {
    (acc[l.group] ??= []).push(l);
    return acc;
  },
  {} as Record<string, ChoroplethLayerDef[]>,
);

export const GROUP_LABELS: Record<string, string> = {
  climate: 'Климат',
  economy: 'Экономика',
  infra: 'Инфраструктура',
  demo: 'Демография',
  tax: 'Налоги',
};

export const DEFAULT_LAYER = 'altitude';
